import {Button, MenuItem, useMediaQuery} from '@material-ui/core';
import {changePolicyFilter, changeSort, clearPolicies, clearSort, fetchPolicies} from 'Agent/Policies/ducks/policy';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import {theme} from 'Core/components/WithTheme';
import {ModalState, showModal} from 'Core/ducks/modal';
import * as O from 'fp-ts/lib/Option';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import Column from 'Shared/components/PagedView/Column';
import {
  withDateFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {getSourceOptions} from 'Shared/constants/Source';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {parseQueryString} from 'Shared/utilities/parsers';
import {maskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';
import {prop} from 'Shared/utilities/prop';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './PoliciesView.sass';

type PoliciesViewProps = {
  selectedAgency: AgencyFilters;
};

const PoliciesView = (props: PoliciesViewProps) => {
  const {policies, policyFilter, sort} = useSelector((state: ReducerState) => state.policy);
  const {selectedAgency} = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const {search} = useLocation();
  const getQueryStringValue = parseQueryString(search);
  const accountNumber = O.getOrElse(() => policyFilter.accountNumber)(getQueryStringValue('accountNumber'));
  const insuredName = O.getOrElse(() => policyFilter.insuredName)(getQueryStringValue('insuredName'));
  const policyNumber = O.getOrElse(() => policyFilter.policyNumber)(getQueryStringValue('policyNumber'));
  const payorName = O.getOrElse(() => policyFilter.payorName)(getQueryStringValue('payorName'));
  const last4PayorNumber = O.getOrElse(() => policyFilter.last4PayorNumber)(getQueryStringValue('last4OfSSN'));
  const last4OfOwnerSSN = O.getOrElse(() => policyFilter.ownerLast4OfSSN)(getQueryStringValue('last4OfOwnerSSN'));
  const scopes = useSelector(selectUserScopes);
  const agentNumbers = useSelector((state: ReducerState) => state.user.profile.currentAgentNumbers);
  const viewPolicyDetail = (policyId: string, policyNumber: string) =>
    history.push(PolicyRouteConstants.POLICY_DETAILS(policyId, policyNumber));

  useEffect(() => {
    dispatch(
      changePolicyFilter({
        ...policyFilter,
        accountNumber,
        insuredName,
        policyNumber,
        last4PayorNumber,
        payorName,
        ownerLast4OfSSN: last4OfOwnerSSN,
      })
    );
  }, [accountNumber, insuredName, policyNumber, payorName, last4PayorNumber, last4OfOwnerSSN]);

  useEffect(() => {
    return () => {
      dispatch(clearPolicies());
      dispatch(clearSort());
    };
  }, [clearPolicies, clearPolicies]);

  useEffect(() => {
    dispatch(
      fetchPolicies(
        agentNumbers,
        selectedAgency.isNewYork,
        selectedAgency.encryptedIdentifier,
        undefined,
        policyFilter,
        O.toUndefined(sort)
      )
    );
  }, [policyFilter, fetchPolicies, selectedAgency, sort]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const changeFilter = withValueChangedHook(policyFilter, changePolicyFilter, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: policyFilter,
  };

  const formattedCsvDownloadSize = Number(parseInt(`${process.env.REACT_APP_POLICY_CSV_DOWNLOAD_SIZE}`)).toLocaleString()
  const policyExtractToolTipMessage = `Only the first ${formattedCsvDownloadSize} records will be extracted`

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredPolicies',
    relativeFileLocation: `/policies/csv${addFilterParameters({
      ...policyFilter,
      agentNumbers: agentNumbers.map(prop('value')),
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
    })}`,
    children: 'Export To CSV',
  };

  return (
    <PagedView
      pagedViewKey="policies"
      sort={O.toUndefined(sort)}
      onChangeSort={sort => {
        dispatch(changeSort(sort));
      }}
      data={policies}
      actions={
        isMobile ? (
          <FileDownloadFab variant="extended" color="primary" displayTooltip tooltipMessage={policyExtractToolTipMessage} {...exportToCsvProps} />
        ) : (
          <FileDownloadButton 
            variant="contained" 
            color="primary" 
            displayTooltip 
            tooltipMessage = {policyExtractToolTipMessage}
          {...exportToCsvProps} />
        )
      }
      loadMore={page =>
        dispatch(
          fetchPolicies(
            agentNumbers,
            selectedAgency.isNewYork,
            selectedAgency.encryptedIdentifier,
            page,
            policyFilter,
            O.toUndefined(sort)
          )
        )
      }
    >
      {columnProps => (
        <>
         <Column
            {...columnProps}
            title="Number"
            sortColumnKey="policyNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'policyNumber')}
            default
          >
            {policy => policy.policyNumber}
          </Column>
         <Column
            {...columnProps}
            title="Account Number"
            sortColumnKey="accountNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
            default
          >
            {policy => policy.accountNumber}
          </Column>
          <Column
            {...columnProps}
            title="Account Name"
            sortColumnKey="accountName"
            filterProperties={withStringFilterProperties(filterProperties, 'accountName')}
            default
          >
            {policy => policy.accountName}
          </Column>         
          <Column
            {...columnProps}
            title="Insured Name"
            sortColumnKey="insuredName"
            filterProperties={withStringFilterProperties(filterProperties, 'insuredName')}
            default
          >
            {policy => policy.insuredName}
          </Column>
          <Column {...columnProps} title="Mode Premium" default>
            {policy => toMoneyValue(policy.premium)}
          </Column>
          <Column
            {...columnProps}
            title="Payor Name"
            sortColumnKey="payorName"
            filterProperties={withStringFilterProperties(filterProperties, 'payorName')}
            default
          >
            {policy => policy.payorName}
          </Column>
          <Column
            {...columnProps}
            title="Coverage Type"
            sortColumnKey="policyType"
            filterProperties={withStringFilterProperties(filterProperties, 'policyType')}
            default
          >
            {policy => policy.policyType}
          </Column>
          <Column
            {...columnProps}
            title="Status"
            sortColumnKey="status"
            filterProperties={withStringFilterProperties(filterProperties, 'status')}
            default
          >
            {policy => policy.status}
          </Column>
          <Column
            {...columnProps}
            title="Effective Date"
            sortColumnKey="effectiveDate"
            filterProperties={withDateFilterProperties(filterProperties, 'effectiveDate')}
            default
          >
            {policy => toFormattedDateAsUTC(policy.effectiveDate)}
          </Column>
          <Column
            {...columnProps}
            title="Source"
            sortColumnKey="source"
            filterProperties={withSelectFilterProperties(
              filterProperties,
              'sourceId',
              getSourceOptions(selectedAgency.isNewYork)
            )}
            default
          >
            {policy => policy.source!.name}
          </Column>
          <Column
            {...columnProps}
            title="Payor SSN"
            sortColumnKey="last4PayorNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'last4PayorNumber')}
          >
            {policy => policy.last4PayorNumber}
          </Column>
          <Column
            {...columnProps}
            title="Issue Date"
            sortColumnKey="issueDate"
            filterProperties={withDateFilterProperties(filterProperties, 'issueDate')}
          >
            {policy => toFormattedDateAsUTC(policy.issueDate)}
          </Column>        
          <Column {...columnProps} title="Owner Name">
            {policy => policy.ownerName}
          </Column>
          <Column {...columnProps} title="Owner Date of Birth">
            {policy => toFormattedDateAsUTC(policy.ownerDateOfBirth)}
          </Column>
          <Column {...columnProps} title="Owner Phone Number">
            {policy => maskPhoneNumber(policy.ownerPhoneNumber)}
          </Column>
          <Column
            {...columnProps}
            filterProperties={withStringFilterProperties(filterProperties, 'ownerLast4OfSSN')}
            title="Owner Last 4 of SSN"
          >
            {policy => policy.ownerLast4OfSSN}
          </Column>
          <Column {...columnProps} title="Owner Email">
            {policy => policy.ownerEmail}
          </Column>
          <Column {...columnProps} title="Owner Address">
            {policy => policy.ownerAddress}
          </Column>
          <Column {...columnProps} title="" isActions default>
            {policy => (
              <div>
                <RequireScope scopes={scopes} scope={Scope.ViewPolicy}>
                  <Button
                    className="primary-action-button"
                    id={'view-policy-details-button-' + policy.policyNumber}
                    onClick={() => viewPolicyDetail(policy.id!, policy.policyNumber!)}
                    color="primary"
                  >
                    View Details
                  </Button>
                </RequireScope>
                <RequireScope scopes={scopes} scope={Scope.SearchClaims}>
                  <ActionsMenu id={`view-policy-claims-btn-${policy.id}`}>
                    <MenuItem
                      onClick={() =>
                        dispatch(showModal(ModalState.ClaimsConfirmation({policyNumber: policy.policyNumber!})))
                      }
                    >
                      Claims
                    </MenuItem>
                  </ActionsMenu>
                </RequireScope>
              </div>
            )}
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default PoliciesView;
